import moment from "moment";
import ExcelJS from "exceljs";
import {
  DynamicChecklistExportDocument,
  ExportFieldRow,
} from "./dynamicChecklistExport";

function verifyStatusCell(row: ExportFieldRow): string {
  if (row.verified === null) return "";
  return row.verified ? "Verified" : "Not verified";
}

/**
 * Generates a checklist Excel workbook from a pre-built dynamic export document.
 */
export async function generateDynamicChecklistExcel(
  listVal: any,
  docModel: DynamicChecklistExportDocument
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Checklist");
  sheet.columns = [{ width: 50 }, { width: 45 }, { width: 18 }];
  sheet.getRow(1).hidden = true;

  const normalizeColor = (variant?: string): string => {
    if (variant === "warning") return "FFFFF3CD";
    return "FF007BFF";
  };

  const addMergedRow = (
    text: string,
    bold = false,
    size = 12,
    bg?: string,
    height = 26
  ) => {
    const row = sheet.addRow([text]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    row.font = { bold, size, color: { argb: bg === "FF007BFF" ? "FFFFFFFF" : "FF000000" } };
    row.height = height;
    if (bg) {
      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bg },
      };
    }
    return row;
  };

  const subsection = (title: string) => {
    const row = sheet.addRow([title]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    row.font = { bold: true };
    row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEEEEEE" } };
    row.height = 24;
  };

  const fieldRow = (label: string, value: string, status: string, indent = 0) => {
    const prefix = indent > 0 ? "  ".repeat(indent) : "";
    const row = sheet.addRow([`${prefix}${label}`, value, status]);
    row.getCell(1).font = { bold: true };
    if (status === "Verified") {
      row.getCell(3).font = { bold: true, color: { argb: "FF28A745" } };
    } else if (status === "Not verified") {
      row.getCell(3).font = { bold: true, color: { argb: "FFD32F2F" } };
    }
  };

  addMergedRow(`${listVal.title} - Checklist Preview`, true, 14, "FF333333", 30);
  addMergedRow(
    `${listVal.file_id} • Login at ${moment(listVal.login_dt).format("DD MMMM YYYY, hh:mm A")} • ${listVal.bank_name}`,
    false,
    11
  );
  addMergedRow(
    `Completed at ${moment(listVal.date).format("DD MMMM YYYY, hh:mm A")} • ${listVal.officerType} • ${listVal.downloadedBy}`,
    false,
    11
  );
  sheet.addRow([]);

  docModel.sections.forEach((section) => {
    addMergedRow(section.title, true, 13, normalizeColor(section.headerVariant));
    section.blocks.forEach((block) => {
      if (block.title) subsection(block.title);
      block.rows.forEach((row) => {
        fieldRow(row.label, row.displayValue, verifyStatusCell(row), row.indent ?? 0);
      });
      sheet.addRow([]);
    });
  });

  if (docModel.remarks) {
    addMergedRow("Remarks", true, 13, "FFFFF3CD");
    const row = sheet.addRow([docModel.remarks]);
    sheet.mergeCells(row.number, 1, row.number, 3);
    row.alignment = { wrapText: true };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${listVal.file_id || "checklist"}_checklist.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}
